import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface StatusEntry {
  status: string;
  updatedAt: string;       // ISO timestamp
  jobPositionId?: number;
  jobName?: string;
  previousStatus?: string;
}

interface CandidateStatusState {
  /** candidateId (string) → latest status entry */
  statuses: Record<string, StatusEntry>;
}

const initialState: CandidateStatusState = {
  statuses: {},
};

const candidateStatusSlice = createSlice({
  name: 'candidateStatus',
  initialState,
  reducers: {
    setStatus(
      state,
      action: PayloadAction<{
        candidateId: string;
        status: string;
        previousStatus?: string;
        jobPositionId?: number;
        jobName?: string;
      }>,
    ) {
      const { candidateId, status, previousStatus, jobPositionId, jobName } = action.payload;
      state.statuses[candidateId] = {
        status,
        updatedAt: new Date().toISOString(),
        previousStatus,
        jobPositionId,
        jobName,
      };
    },

    /** Bulk-seed statuses from an API list response (no log entries created) */
    seedStatuses(
      state,
      action: PayloadAction<Array<{ candidateId: string; status: string }>>,
    ) {
      action.payload.forEach(({ candidateId, status }) => {
        // Only seed if we don't already have a more recent local update
        if (!state.statuses[candidateId]) {
          state.statuses[candidateId] = {
            status,
            updatedAt: new Date(0).toISOString(), // epoch = lowest priority
          };
        }
      });
    },
  },
});

export const { setStatus, seedStatuses } = candidateStatusSlice.actions;
export default candidateStatusSlice.reducer;

/* ── Selectors ─────────────────────────────────────────────── */
export const selectCandidateStatus = (
  state: { candidateStatus: CandidateStatusState },
  candidateId: string | number,
): string | null =>
  state.candidateStatus.statuses[String(candidateId)]?.status ?? null;
